import { useNetwork } from 'ahooks';
import { fetchUsersBoard } from 'API';
import {
  fb,
  gapiAuthInstance,
  getFacebookProfile,
  getGoogleProfile,
} from 'auth';
import { useEffect, useRef } from 'react';
import { useRecoilCallback, useRecoilState, useSetRecoilState } from 'recoil';
import { UserProfileType } from 'Types';

import BoardBase from 'boardClasses/BoardBase';
import {
  boardInstanceAtomFamily,
  cloudBoardIDsAtom,
  isOnlineAtom,
} from 'state/recoilState';
import { facebookUserAtom, googleUserAtom } from 'state/signInRecoilState';
import { isDev, loadScript } from 'utils/utils';

function AuthEffects() {
  const [googleUser, setGoogleUser] = useRecoilState(googleUserAtom);
  const googleToken = googleUser?.accessToken ?? null;

  const [facebookUser, setFacebookUser] = useRecoilState(facebookUserAtom);
  const facebookToken = facebookUser?.accessToken ?? null;

  const isOnline = useNetwork().online ?? false;
  const setIsOnline = useSetRecoilState(isOnlineAtom);

  const googleScriptRef = useRef<HTMLScriptElement | null>(null);
  const facebookScriptRef = useRef<HTMLScriptElement | null>(null);

  const setCloudBoards = useRecoilCallback(
    ({ set }) =>
      (boards: Array<BoardBase>) => {
        set(
          cloudBoardIDsAtom,
          boards.map(({ id }) => id)
        );

        for (const board of boards) {
          set(boardInstanceAtomFamily(board.id), board);
        }
      }
  );

  const onGoogleStatusChange = (user: gapi.auth2.GoogleUser) => {
    setGoogleUser(getGoogleProfile());
  };

  const onFacebookStatusChange = async () => {
    const foo = await getFacebookProfile();
    setFacebookUser(foo);
  };

  const onGoogleAPILoaded = () => {
    gapi.load('auth2', function () {
      gapi.auth2.init({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      });

      gapiAuthInstance()?.currentUser.listen(onGoogleStatusChange);
    });
  };

  const onFacebookAPILoaded = async () => {
    FB.init({
      appId: process.env.REACT_APP_FACEBOOK_APP_ID,
      autoLogAppEvents: false,
      version: 'v9.0',
      status: true,
    });

    FB.Event.subscribe('auth.authResponseChange', onFacebookStatusChange);
  };

  // Load google and facebook javascript
  useEffect(() => {
    if (!isDev()) {
      return;
    }

    if (googleScriptRef.current == null) {
      googleScriptRef.current = loadScript(
        'https://apis.google.com/js/platform.js',
        onGoogleAPILoaded
      );
    }

    if (facebookScriptRef.current == null) {
      facebookScriptRef.current = loadScript(
        'https://connect.facebook.net/en_US/sdk.js',
        onFacebookAPILoaded
      );
    }
  });

  useEffect(() => {
    setIsOnline(isOnline);
  }, [isOnline, setIsOnline]);

  useEffect(() => {
    if (facebookUser?.accessToken == null) {
      return;
    }

    fb()?.api(
      '/me',
      { fields: ['picture', 'name', 'email'] },
      ({
        id,
        name,
        picture,
      }: {
        id: string;
        name: string;
        picture: { data: { url: string } };
      }) => {
        setFacebookUser({
          type: UserProfileType.FACEBOOK,
          id,
          name,
          image: picture.data.url,
          accessToken: facebookUser.accessToken,
        });
      }
    );
  }, [facebookUser?.accessToken, setFacebookUser]);

  // fetch boards for google/facebook account
  useEffect(() => {
    if (googleToken == null && facebookToken == null) {
      return;
    }
    fetchUsersBoard({ googleToken, facebookToken }).then(setCloudBoards);
  }, [googleToken, facebookToken, setCloudBoards]);

  return null;
}

export default AuthEffects;
